import { Badge, Col, Flex, FormatDate, Row, Text } from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import * as React from 'react';
import { ContainerOrderTransportItemStatus } from '~/common/components';
import { Trans, useLocale } from '~/common/context';
import type { IContainerOrder } from '~/common/types';
import { ValueInfoItem } from '~/purchase/components';

type ViewDisplayProps = {
  item: IContainerOrder['resolvedItems'][number];
  order?: Pick<IContainerOrder, 'updatedFieldsByItemId'>;
};

export const ViewDisplay: React.FC<ViewDisplayProps> = ({ item, order }) => {
  const locale = useLocale();
  const isDateUpdated =
    order?.updatedFieldsByItemId?.[item.uuid]?.deliveryDate ?? false;
  const isQuantityUpdated =
    order?.updatedFieldsByItemId?.[item.uuid]?.totalQuantity ?? false;
  const isPriceUpdated =
    order?.updatedFieldsByItemId?.[item.uuid]?.totalPrice ?? false;
  return (
    <Row className='align-items-center gy-3'>
      <Col xs={12} sm={3}>
        <Text
          heading='normal'
          weight='bold'
          color='tertiary-bold'
          id={`container-${item.reference}`}
        >
          <Trans
            i18nKey='app.container.order.detail.container_number'
            values={{ number: item.reference || 'N/A' }}
          />
        </Text>
        <Text
          heading='sm'
          weight='semi-bold'
          color='secondary-gray'
          id={`container-id-${item.reference}`}
        >
          <Trans
            i18nKey='app.container.order.detail.container_id'
            values={{ id: item.id }}
          />
        </Text>
      </Col>
      <Col xs={12} sm={2}>
        <Flex justifyContent='center'>
          <Text id={`status-${item.reference}`}>
            <ContainerOrderTransportItemStatus status={item.status} />
          </Text>
        </Flex>
      </Col>
      <Col xs={12} sm={7} className='d-flex align-items-end'>
        <Flex
          className='w-100 space-x-2 space-y-2'
          alignItems='center'
          justifyContent='between'
          wrap='wrap'
        >
          <ValueInfoItem
            as='div'
            title={<Trans i18nKey='app.purchase.orders.table.delivery_date' />}
            align='right'
          >
            <Text
              heading='normal'
              weight='semi-bold'
              color='secondary'
              className='mt-2 text-right'
              id={`delivery-date-display-${item.reference}`}
            >
              {isDateUpdated ? (
                <Badge bg='warning'>
                  <FormatDate
                    date={item.deliveryDate}
                    format={DateUtils.DATE_FORMAT.DMY}
                    locale={locale.locale}
                  />
                </Badge>
              ) : (
                <FormatDate
                  date={item.deliveryDate}
                  format={DateUtils.DATE_FORMAT.DMY}
                  locale={locale.locale}
                />
              )}
            </Text>
          </ValueInfoItem>
          <ValueInfoItem
            as='div'
            title={<Trans i18nKey='app.common.total_quantity' />}
            align='right'
          >
            <Text
              heading='normal'
              weight='semi-bold'
              color='secondary'
              className='mt-2 text-right'
              id={`quantity-${item.reference}`}
            >
              {isQuantityUpdated ? (
                <Badge bg='warning'>
                  {locale.formatters.quantity(item.totalQuantity ?? 0)}
                </Badge>
              ) : (
                locale.formatters.quantity(item.totalQuantity ?? 0)
              )}
            </Text>
          </ValueInfoItem>
          <ValueInfoItem
            as='div'
            title={<Trans i18nKey='app.common.total_price' />}
            align='right'
          >
            <Text
              heading='normal'
              weight='semi-bold'
              color='secondary'
              className='mt-2 text-right'
              id={`quantity-${item.reference}`}
            >
              {isPriceUpdated ? (
                <Badge bg='warning'>
                  {locale.formatters.money(item.totalPrice ?? 0)}
                </Badge>
              ) : (
                locale.formatters.money(item.totalPrice ?? 0)
              )}
            </Text>
          </ValueInfoItem>
        </Flex>
      </Col>
    </Row>
  );
};
